import { Breadcrumbs, Button, IconButton, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { PageContent, PageHeader, RenderIf } from "@adl/smart-ui-library";
import { useAppSelector, useAppDispatch } from "@/hooks";
import {
  setSelectedCase,
  selectSelectedCase,
  selectSelectedDocument,
  setSelectedDocument,
  selectEditsToSave,
  putDocumentDetails,
} from "@/redux/casesSlice";
import DefaultTemplate from "@/templates/DefaultTemplate/DefaultTemplate";

import { setSelectedTasks } from "@/redux/tasksSlice";
import Notifications from "@/components/Notifications";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { useMsal } from "@azure/msal-react";
import DocumentProfileWrapper from "../../components/DocumentProfileWrapper";
import SaveChangesModal from "@/components/SaveChangesModal";
import DiscardChangesModal from "@/components/DiscardChangesModal";
import { setCaseEditsToSave } from "../../redux/casesSlice";
import VaultIcon from "../../components/VaultIcon/VaultIcon";
import VaultTextLogo from "@/components/VaultTextLogo";
import { useNavigate } from "react-router-dom";

const SelectedDocumentPage = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();
  const caseProfile = useAppSelector(selectSelectedCase);
  const docProfile = useAppSelector(selectSelectedDocument);
  const editsToSave = useAppSelector(selectEditsToSave);
  const [saveModalOpen, setSaveModalOpen] = React.useState<boolean>(false);
  const [continueModalOpen, setContinueModalOpen] = React.useState<any | null>(
    null
  );

  useEffect(() => {
    if (!caseProfile || !docProfile) {
      navigate(`/`);
    }
  }, []);

  const onLogout = () => {
    instance.logout();
  };

  const onTaskBtnClick = () => {
    if (editsToSave) {
      setContinueModalOpen(() => () => appDispatch(setSelectedTasks([])));
    } else {
      appDispatch(setSelectedTasks([]));
      navigate(`/tasks`);
    }
  };

  const onHomeBtnClick = () => {
    if (editsToSave) {
      setContinueModalOpen(() => () => appDispatch(setSelectedCase(null)));
    } else {
      appDispatch(setSelectedCase(null));
      navigate(`/`);
    }
  };

  const navigateToCases = () => {
    if (editsToSave) {
      setContinueModalOpen(() => () => appDispatch(setSelectedDocument(null)));
    } else {
      appDispatch(setSelectedDocument(null));
      const basePath = location.pathname.split("/document")[0];
      navigate(basePath);
    }
  };

  const onSaveRequest = () => {
    if (editsToSave) {
      setSaveModalOpen(true);
    }
  };

  const onSave = () => {
    appDispatch(
      putDocumentDetails({
        deliveryEngagementId: caseProfile?.DeliveryEngagementId,
        payload: editsToSave,
      })
    );
    appDispatch(setSelectedDocument(editsToSave));
    appDispatch(setCaseEditsToSave(null));
    setSaveModalOpen(false);
  };

  const onContinue = () => {
    continueModalOpen(null);
  };

  const isValidated = docProfile?.fileStatus === "verified";

  if (!caseProfile || !docProfile) {
    return null;
  }

  return (
    <DefaultTemplate paddingTop={200}>
      <>
        <PageHeader
          logo={
            <VaultIcon width={40} height={40} handleClick={onHomeBtnClick} />
          }
          navigation={
            <>
              <Notifications />
              <Button onClick={onTaskBtnClick}>Tasks</Button>
              <Button onClick={onLogout}>Logout</Button>
            </>
          }
          subHeader={
            <>
              <Breadcrumbs
                aria-label="breadcrumb"
                separator={<NavigateNextIcon fontSize="small" />}
              >
                <IconButton onClick={onHomeBtnClick}>
                  <HomeOutlinedIcon />
                </IconButton>
                <Button variant="text" onClick={navigateToCases}>
                  {caseProfile?.P_number}
                </Button>
                <Typography color="text.primary">
                  {docProfile?.fileNameOriginal}
                </Typography>
              </Breadcrumbs>
              <RenderIf isTrue={!isValidated}>
                <Button
                  variant="contained"
                  onClick={onSaveRequest}
                  disableElevation
                  disabled={Boolean(!editsToSave)}
                >
                  Save Changes
                </Button>
              </RenderIf>
            </>
          }
        >
          <VaultTextLogo />
        </PageHeader>
        <PageContent>
          <DocumentProfileWrapper />
        </PageContent>
        <SaveChangesModal
          open={Boolean(saveModalOpen)}
          onClose={() => setSaveModalOpen(false)}
          onSave={onSave}
        />
        <DiscardChangesModal
          open={Boolean(continueModalOpen)}
          onClose={() => setContinueModalOpen(false)}
          onContinue={onContinue}
        />
      </>
    </DefaultTemplate>
  );
};

export default SelectedDocumentPage;
